import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryBtn from '../shared/Buttons/PrimaryButton';
import { CartResponse } from '../../models/Cart';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    styled,
    Typography,
} from '@mui/material';
import { getCurrency, getNetPrice, isNullOrUndefined } from '../../utils/utils';
import { formatPrice } from '../../utils/numberFormatter';
import { SubmitHandler, useForm } from 'react-hook-form';
import { B2B_URL, LEGAL_SITE_URLS } from '../../utils/constans/constans';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useShopifyCustomer from '../../hooks/shopify/useShopifyCustomer';
import storageService, { Locals } from '../../utils/storageService';
import useShopifyCart from '../../hooks/shopify/useShopifyCart';

const schema = yup.object().shape({
    terms_of_service: yup.boolean().oneOf([true], 'REQUIRED'),
    statement: yup.boolean().oneOf([true], 'REQUIRED'),
});

const StyledPriceInfo = styled(Typography)(() => ({
    color: '#868686',
    paddingTop: '10px',
}));

export const CartSummaries = (props: { cart: CartResponse }) => {
    const { t } = useTranslation('cart', {
        keyPrefix: 'CART',
    });
    const token = storageService.getItem(Locals.SHOPIFY_ACCESS_TOKEN);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
    });
    const shopifyCart = useShopifyCart();
    const { cart } = props;
    const { customer, isLoading, isError, isValidating } =
        useShopifyCustomer().getCustomerSWR(token);
    const [value, setValue] = React.useState(
        cart.attributes.find((elem) => elem.key == 'payment_method').value
    );
    const [isBanking, setIsBanking] = React.useState(false);
    const termAndConditions = B2B_URL + LEGAL_SITE_URLS.TERMS_AND_CONDITIONS;
    const privacyStatement = B2B_URL + LEGAL_SITE_URLS.PRIVACY_STATEMENT;

    const redirect = () => {
        window.location.replace(cart.checkoutUrl + '?step=contact_information');
    };
    const goToCheckout: SubmitHandler<FormValues> = (data) => {
        if (isBanking && !isNullOrUndefined(data.payment_method)) {
            ///update
            shopifyCart
                .updateAttributes([
                    {
                        key: 'payment_method',
                        value: data.payment_method,
                    },
                ])
                .then(() => {
                    redirect();
                });
        } else {
            redirect();
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue((event.target as HTMLInputElement).value);
    };

    useEffect(() => {
        if (!isNullOrUndefined(customer)) {
            const metafield = customer.metafields.find(
                (elem) => elem.key == 'bc_payment_method_code'
            );
            if (metafield) {
                if (metafield.value == 'BANK') {
                    setIsBanking(true);
                } else {
                    setValue(metafield.value);
                }
            } else {
                setValue('UTANVET');
            }
        }
    }, [customer, isLoading, isValidating]);

    return (
        <form onSubmit={handleSubmit(goToCheckout)}>
            <Card>
                <CardContent>
                    <Typography
                        variant={'h2'}
                        sx={{
                            borderBottom: '1px solid black',
                            paddingBottom: '10px',
                            fontWeight: 'normal',
                        }}
                    >
                        {t('ORDER_SUMMARY')}
                    </Typography>
                    <Box
                        component={'span'}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            paddingY: '15px',
                            borderBottom: '1px solid #868686',
                        }}
                    >
                        <Typography
                            variant={'h3'}
                            sx={{
                                fontWeight: 'normal',
                            }}
                        >
                            {t('TOTAL')}:
                        </Typography>
                        <Typography
                            variant={'h3'}
                            sx={{ fontWeight: 'normal' }}
                        >
                            {formatPrice(
                                getNetPrice(
                                    Number(cart.cost.totalAmount.amount)
                                )
                            )}{' '}
                            {getCurrency(cart.cost.totalAmount.currencyCode)}
                        </Typography>
                    </Box>
                    <StyledPriceInfo>{t('PRICE_INFO')}</StyledPriceInfo>
                    <Typography sx={{ paddingTop: '15px' }}>
                        {t('PAYING_INFO')}
                    </Typography>
                    <FormControl fullWidth>
                        <FormControlLabel
                            control={
                                <Checkbox {...register('terms_of_service')} />
                            }
                            label={
                                <>
                                    <p>
                                        {t('TERMS_OF_SERVICE').toString()}
                                        <a
                                            href={termAndConditions}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t(
                                                'TERMS_OF_SERVICE_LINK'
                                            ).toString()}
                                        </a>
                                        {t('AND').toString()}
                                        <a
                                            href={privacyStatement}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t('ASZF').toString()}
                                        </a>
                                    </p>
                                </>
                            }
                        />
                        <FormHelperText error={!!errors.terms_of_service}>
                            {errors.terms_of_service &&
                                t(errors.terms_of_service.message)}
                        </FormHelperText>
                    </FormControl>
                    <FormControl fullWidth>
                        <FormControlLabel
                            control={<Checkbox {...register('statement')} />}
                            label={
                                <Typography>
                                    {t('STATEMENT').toString()}
                                </Typography>
                            }
                        />
                        <FormHelperText error={!!errors.statement}>
                            {errors.statement && t(errors.statement.message)}
                        </FormHelperText>
                    </FormControl>
                    {isBanking && (
                        <FormControl sx={{ paddingTop: '10px' }}>
                            <FormLabel id="demo-controlled-radio-buttons-group">
                                {t('CHOOSE_PAYMENT_METHOD')}
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="demo-controlled-radio-buttons-group"
                                name="controlled-radio-buttons-group"
                                value={value}
                                onChange={handleChange}
                            >
                                <FormControlLabel
                                    value="UTANVET"
                                    control={
                                        <Radio
                                            {...register('payment_method')}
                                        />
                                    }
                                    label={t('CASH')}
                                />
                                <FormControlLabel
                                    value="BANK"
                                    control={
                                        <Radio
                                            {...register('payment_method')}
                                        />
                                    }
                                    label={t('BANK')}
                                />
                            </RadioGroup>
                        </FormControl>
                    )}
                </CardContent>
                <CardActions>
                    <PrimaryBtn
                        type="submit"
                        sx={{ flex: '1' }}
                        disabled={
                            Number(cart.cost.totalAmount.amount) == 0 ||
                            isLoading
                        }
                    >
                        {t('ORDER_BUTTON')}
                    </PrimaryBtn>
                </CardActions>
            </Card>
        </form>
    );
};

type FormValues = {
    terms_of_service: boolean;
    statement: boolean;
    payment_method: string;
};
